// Correlation verdict card — renders the cached heavy-stat suite from
// GET /api/safety/verdict (permutation p-values, observed-vs-expected in-run
// counts, rate ratios). Unlike the descriptive panels this does NOT react to
// filters: it is computed once per corridor+crash-dataset over the full
// unfiltered data, and says so.
//
// Reading the verdict correctly: the test does NOT expect a crash at every
// deficiency. It asks one question — do crashes concentrate at deficiency
// locations MORE than random placement would, given how much of the corridor
// is deficient? "At chance" means the observed count is about what random
// placement yields for runs covering that much roadway, not "no crashes at
// deficiencies".
import React from 'react';

const CHIP = {
  associated: { label: 'Above chance', className: 'safety-chip safety-chip-bad' },
  not_associated: { label: 'At chance', className: 'safety-chip safety-chip-ok' },
  underpowered: { label: 'Too few crashes', className: 'safety-chip safety-chip-warn' },
  no_station_data: { label: 'No data', className: 'safety-chip safety-chip-neutral' },
  descriptive: { label: 'Descriptive', className: 'safety-chip safety-chip-neutral' },
};

const SUBSET_LABEL = {
  non_animal: 'non-animal',
  wet_surface: 'wet/snow surface',
  severe_embankment: 'severe embankment',
  passing_related: 'head-on / opposite-direction',
};

/** Verdict outcomes the Statistical support filter can pick from. */
export const VERDICT_OPTIONS = [
  { key: 'associated', label: 'Above chance' },
  { key: 'not_associated', label: 'At chance' },
  { key: 'underpowered', label: 'Too few crashes' },
  { key: 'no_station_data', label: 'No data' },
];

const fmt = (v, digits = 3) => (v === null || v === undefined ? '—' : Number(v).toFixed(digits));

export default function VerdictCard({ verdict, loading, error, onRecompute, recomputing, show = [] }) {
  if (loading) return <div className="safety-muted">Computing correlation verdict…</div>;
  if (error) return <div className="safety-error">Verdict unavailable: {error}</div>;
  if (!verdict) return null;

  return (
    <div className="safety-verdict">
      <p className="safety-verdict-text">{verdict.overall?.text}</p>
      <p className="safety-muted safety-verdict-power">
        Supporting evidence only — correlations are identified per crash in Insights
        (a crash at a deficiency IS the finding). This table asks the corridor-wide
        question: do crashes concentrate at deficiency locations more than random
        placement would, given how much roadway each analysis flags? It does not
        expect a crash at every deficiency.
        {verdict.overall?.powerNote ? ` ${verdict.overall.powerNote}` : ''}
      </p>
      <div className="safety-table-scroll">
      <table className="safety-table">
        <thead>
          <tr>
            <th title="RDV analysis whose deficiency runs are tested">Analysis</th>
            <th title="Above chance: crashes concentrate at this analysis' deficiencies more than random placement would (p ≤ 0.05 on either test). At chance: not distinguishable from random placement (needs ≥ 30 crashes to say so). Too few crashes: under 30 tested crashes and no significant signal. No data: no runs for this analysis on the corridor.">Verdict</th>
            <th title="Crashes entering the test: the mechanism-matched subset (all non-animal crashes for SSD/HCA/ISD/GLR, wet-surface for FSA, head-on / opposite-direction for PSD, severe embankment for GRW) over the full corridor and every loaded year">Crashes tested</th>
            <th title="obs = crashes observed within ±0.1 mi of a deficiency run (the site buffer). exp = the average count the same number of crashes produced when dropped at random survey stations along the corridor, over 5,000 draws">In runs (obs / exp)</th>
            <th title="Share of the 5,000 random placements that put at least as many crashes inside the runs as observed (+1 correction). Small = the observed concentration is unlikely by chance; ≤ 0.05 counts as significant.">p (count)</th>
            <th title="Observed mean distance from each tested crash to its nearest deficiency run, vs the mean the random placements produced">Mean nearest (mi)</th>
            <th title="Share of the 5,000 random placements whose mean crash-to-run distance was as small as observed (+1 correction). Small = crashes sit nearer the deficiencies than chance; ≤ 0.05 counts as significant.">p (distance)</th>
            <th title="Crashes per mile inside the buffered runs ÷ crashes per mile on the rest of the corridor. 2.0 = twice the crash density at the deficiencies. Descriptive only; not part of the verdict.">Rate ratio</th>
            <th>Notes</th>
          </tr>
        </thead>
        <tbody>
          {Object.entries(verdict.perAnalysis || {})
            .filter(([, v]) => !show.length || show.includes(v.verdict || 'underpowered'))
            .map(([analysis, v]) => {
            const chip = CHIP[v.verdict] || CHIP.underpowered;
            return (
              <tr key={analysis}>
                <td>{analysis}</td>
                <td><span className={chip.className}>{chip.label}</span></td>
                <td>{v.nCrashes}{v.crashSubset && v.crashSubset !== 'non_animal'
                  ? ` (${SUBSET_LABEL[v.crashSubset] || v.crashSubset})` : ''}</td>
                <td title={v.nInRuns != null ? `${v.nInRuns} crashes observed within ±0.1 mi of a run; random placement of the same ${v.nCrashes} crashes gives ~${v.expectedInRuns} on average` : ''}>
                  {v.nInRuns != null ? `${v.nInRuns} / ~${v.expectedInRuns}` : '—'}</td>
                <td title={v.countP != null ? `${Math.round(v.countP * 5001 - 1)} of 5,000 random placements reached ≥ ${v.nInRuns} crashes inside the runs → p = ${fmt(v.countP)}${v.countP <= 0.05 ? ' (significant)' : ''}` : ''}>{fmt(v.countP)}</td>
                <td title={v.meanNearestMi != null ? `observed ${fmt(v.meanNearestMi)} mi vs ${fmt(v.expectedMeanMi)} mi under random placement` : ''}>{v.meanNearestMi != null
                  ? `${fmt(v.meanNearestMi)} vs ${fmt(v.expectedMeanMi)}` : '—'}</td>
                <td title={v.pValue != null ? `${Math.round(v.pValue * 5001 - 1)} of 5,000 random placements had a mean distance ≤ ${fmt(v.meanNearestMi)} mi → p = ${fmt(v.pValue)}${v.pValue <= 0.05 ? ' (significant)' : ''}` : ''}>{fmt(v.pValue)}</td>
                <td title={v.rateInside != null && v.rateOutside != null ? `${v.rateInside} crashes/mi inside the buffered runs ÷ ${v.rateOutside} crashes/mi outside = ${fmt(v.rateRatio, 2)}` : ''}>{fmt(v.rateRatio, 2)}</td>
                <td className="safety-verdict-note">
                  {analysis === 'GLR' && v.temporalMatches != null
                    ? `${v.temporalMatches} crash${v.temporalMatches === 1 ? '' : 'es'} inside glare impact ranges`
                    : v.note || '—'}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
      </div>
      <div className="safety-verdict-foot">
        <span className="safety-muted">
          Computed {verdict.computedAt ? verdict.computedAt.slice(0, 10) : '—'} on
          dataset {verdict.datasetVersion} · full corridor, animal strikes excluded ·
          5,000-draw permutation test
        </span>
        <button type="button" className="safety-btn" onClick={onRecompute} disabled={recomputing}>
          {recomputing ? 'Recomputing…' : 'Recompute'}
        </button>
      </div>
    </div>
  );
}
